import { useRef, useState, useEffect } from "react";
import { PlayIcon, PauseIcon, ArrowRight, XIcon } from "lucide-react";
import tv from "../../images/tv.jpg"


export default function TemplateCard({ t, handleUseTemplate }) {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [showPreview, setShowPreview] = useState(false);
    const [isFrameLoaded, setIsFrameLoaded] = useState(false);
    const [isVideoReady, setIsVideoReady] = useState(true);

    // 🔥 Hide controls after 2 sec when playing
    useEffect(() => {
        let timeout;

        if (isPlaying) {
            timeout = setTimeout(() => {
                setShowControls(false);
            }, 1000);
        }

        return () => clearTimeout(timeout);
    }, [isPlaying]);

    const handleToggle = () => {
        if (!videoRef.current) return;

        if (isPlaying) {
            videoRef.current.pause();
            setShowControls(true);
        } else {
            videoRef.current.currentTime = 0;
            videoRef.current.play();
            setShowControls(true);
        }

        setIsPlaying(!isPlaying);
    };

    const handleMouseMove = () => {
        if (isPlaying) {
            setShowControls(true);
        }
    };

    const handleMouseLeave = () => {
        if (isPlaying) {
            setShowControls(false);
        }
    };


    return (
        <>
            <div className="group relative w-full mx-auto bg-white p-4 pb-0 rounded-2xl border border-slate-200 shadow-xl transition-all duration-300 hover:shadow-2xl hover:border-slate-300">
                <div className="relative w-full">
                    {(!isFrameLoaded || !isVideoReady) && (
                        <div className="absolute inset-0 rounded-xl overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 animate-pulse" />
                        </div>
                    )}

                    <img
                        src={tv}
                        className={`w-full h-auto block pointer-events-none transition-opacity duration-300 ${isFrameLoaded ? 'opacity-100' : 'opacity-0'}`}
                        alt="TV Frame"
                        onLoad={() => setIsFrameLoaded(true)}
                    />

                    <div
                        className={`absolute overflow-hidden bg-black transition-opacity duration-300 ${isFrameLoaded && isVideoReady ? 'opacity-100' : 'opacity-0'}`}
                        style={{
                            top: 'clamp(2%, 2.8%, 4%)',
                            left: 'clamp(0.5%, 1.8%, 3.5%)',
                            right: 'clamp(0.5%, 1.8%, 3.5%)',
                            bottom: 'clamp(8%, 11.97%, 14%)',
                        }}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={handleMouseLeave}
                    >

                        <video
                            ref={videoRef}
                            src={t.videoURL}
                            className="w-full h-full object-cover"
                            muted
                            playsInline
                            preload="metadata"
                            onEnded={() => setIsPlaying(false)}
                            onLoadedMetadata={(e) => {
                                if (e.target) {
                                    e.target.currentTime = t.currentTime;
                                }
                            }}
                            onLoadedData={() => setIsVideoReady(true)}
                            onCanPlay={() => setIsVideoReady(true)}
                            onError={() => setIsVideoReady(true)}
                        />

                        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${showControls || !isPlaying ? 'opacity-100' : 'opacity-0'}`}>

                            <button
                                onClick={handleToggle}
                                className="bg-black/40 backdrop-blur-sm text-white rounded-full h-14 w-14 flex items-center justify-center border border-white/30 hover:scale-110 active:scale-95 transition-all hover:bg-black/60"
                            >
                                {isPlaying ? (
                                    <PauseIcon className="w-6 h-6" />
                                ) : (
                                    <PlayIcon className="w-6 h-6 ml-1" />
                                )}
                            </button>

                        </div>

                        <button
                            onClick={() => setShowPreview(true)}
                            className="absolute bottom-[0] right-[0] z-20 bg-black/40 backdrop-blur-sm text-white  p-0.5 transition-all duration-200 hover:bg-black/70 shadow-lg"
                            aria-label="Fullscreen Preview"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                        </button>

                    </div>
                </div>

                <div className="px-6 py-4 -mx-4 mt-4 border-t border-slate-200/70 flex justify-between items-center gap-2">
                    <div>
                        <h3 className="text-xl font-semibold text-slate-800 leading-tight mb-1">
                            {t.name}
                        </h3>
                        <div className="w-max bg-gradient-to-r from-indigo-50 to-purple-50 px-2 py-1 rounded-full border border-indigo-100 text-xs font-semibold tracking-wider text-indigo-600">
                            {t.tag}
                        </div>
                    </div>

                    <button
                        onClick={() => handleUseTemplate(t)}
                        className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-slate-800 transition dashboard-btn-new"
                    >
                        Use Template
                        <ArrowRight className="h-4 w-4 ml-1" />
                    </button>
                </div>
            </div>

            {showPreview && (
                <div className="fixed inset-0 z-[1111] flex items-center justify-center p-3 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="relative w-full max-w-7xl bg-white rounded-2xl overflow-hidden shadow-2xl">
                        {/* Header - Light Theme */}
                        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-white">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow-md">
                                    <PlayIcon className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold">
                                        Video Preview
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        {t?.name}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowPreview(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-200 text-gray-500 hover:text-gray-700"
                            >
                                <XIcon className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Video Player */}
                        <div className="relative bg-black">
                            <div className="aspect-video">
                                <video
                                    src={t?.videoURL}
                                    className="w-full h-full object-contain"
                                    controls
                                    autoPlay
                                    playsInline
                                    preload="auto"
                                />
                            </div>
                        </div>

                        {/* Footer - Light Theme */}
                        <div className="px-5 py-2.5 bg-white border-t border-gray-200">
                            <div className="flex justify-between items-center gap-4">
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
                                    <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
                                    </svg>
                                    <span className="text-sm text-gray-700">{t?.tag}</span>
                                </div>

                                <button
                                    onClick={() => {
                                        setShowPreview(false)
                                        handleUseTemplate(t)
                                    }}
                                    className="group inline-flex items-center gap-2 rounded-lg border border-slate-200 hover:bg-gray-100  px-4 py-2 text-sm font-medium text-gray-700 "
                                >
                                    Use Template
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}